import { isFrictionlessSignupUser } from '@frontend/api-client';
import { match } from '@frontend/duck-tape';
import { type DraftTaskEntryPoint, useVisibilityController } from '@frontend/react';
import type { DraftParentStepProps, InitialQuestionWithAnswerV2 } from '@frontend/react-domain';
import { getIQTitle, useCheckoutStep } from '@frontend/react-domain';
import { Button, Divider, FilePreviewsList, Icon, Text, XStack, YStack, showToast } from '@frontend/web-react';
import { usePricingTerminology } from '@frontend/web/context';
import {
  useAnalytics,
  useCreateTaskFromDraftTaskMutation,
  useRetrieveClientQuery,
  useRetrieveClientStatusQuery,
  useRetrieveCreditsQuery,
} from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { BuyCreditsModal } from '../../Payment/BuyCreditsModal/BuyCreditsModal';
import { PaywallModal } from '../../Paywall';
import { SectionCard } from './SectionCard';

export const Checkout = ({ entryPoint, incrementParentStep, startOver }: DraftParentStepProps & { entryPoint: DraftTaskEntryPoint; startOver: () => void }) => {
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const { data: client } = useRetrieveClientQuery();
  const { data: credits, refetch: refetchCredits } = useRetrieveCreditsQuery();
  const { refetch: refetchClientStatus } = useRetrieveClientStatusQuery();
  const { pricingTerminology, showCredits } = usePricingTerminology();
  const [createTaskFromDraftMutation] = useCreateTaskFromDraftTaskMutation();
  const [isBuyCreditsModalOpen, setBuyCreditsModalOpen] = useState(false);
  const [isPaywallModalOpen, { close: hidePaywallModal, open: showPaywallModal }] = useVisibilityController();

  const {
    checkoutButtonType,
    createTask,
    creditText,
    getTaskStartText,
    isCreateTaskLoading,
    state,
    taskComplexityText,
    taskCreditCost,
  } = useCheckoutStep({
    createTaskFromDraftMutation,
    credit: credits,
    handleMutation,
    incrementParentStep,
    onTaskCreated: () => {
      track({
        data: { entryPoint },
        event: 'draftTaskCheckoutCompleted',
        topic: 'draftTasks',
      });
      showToast({
        title: 'Task is scheduled',
        variant: 'success',
      });
      refetchCredits();
      navigate({ to: '/app/home' });
    },
    useRetrieveClientQuery,
    useRetrieveClientStatusQuery,
  });

  const taskStartText = getTaskStartText();
  const balance = credits?.availableCredits ?? 0;
  const creditsLoaded = credits !== undefined;
  const isShortOnCredits = creditsLoaded && balance < taskCreditCost;

  const CheckoutButton = () =>
    match(checkoutButtonType)
      .with('addPaymentMethod', () => (
        <Button fullWidth onClick={showPaywallModal} variant="primary">
          {`Get ${pricingTerminology.unit_term_plural} & Submit`}
        </Button>
      ))
      .with('createTask', () => (
        <Button fullWidth isLoading={isCreateTaskLoading} onClick={createTask} variant="gradient">
          {taskCreditCost ? `Use ${creditText.cost}` : 'Create free task'}
        </Button>
      ))
      .with('buyCredits', () => (
        <Button fullWidth onClick={() => setBuyCreditsModalOpen(true)} variant="primary">
          {pricingTerminology.insufficient_label}
        </Button>
      ))
      .exhaustive();

  const onPaywallSuccess = async () => {
    await Promise.allSettled([refetchCredits(), refetchClientStatus()]);
    hidePaywallModal();
  };

  return (
    <YStack className="grid grid-cols-1 lg:grid-cols-8 md:grid-cols-4 gap-x-md gap-y-md px-md">
      <YStack className="gap-y-sm col-span-full lg:col-span-4 lg:col-start-3">
        <Text type="h4">Summary</Text>
      </YStack>

      <YStack className="lg:col-span-2 lg:col-start-3 md:col-span-2">
        <YStack className="gap-y-md">
          <SectionCard icon="IconAlignLeft" onPressEdit={startOver} title="Description">
            <YStack className="gap-y-md">
              <YStack className="gap-y-md">
                <Text color="secondary">{state.description}</Text>
                <FilePreviewsList
                  files={
                    state.draftTaskEntity?.attachmentUrls.map((a) => ({
                      ...a,
                      __typename: 'SavedFile',
                      key: a.id,
                      url: a.contentUrl,
                    })) ?? []
                  }
                />
              </YStack>
              <XStack gapX="sm" justifyContent="start">
                <Icon className="self-start" color="secondary" name="IconInfoCircleFilled" size={14} />
                <Text color="secondary" type="p-sm">
                  Changing the description will reset your progress and you&apos;ll need to go through the questions
                  again.
                </Text>
              </XStack>
            </YStack>
          </SectionCard>

          <SectionCard icon="IconStack" title="Detail">
            <YStack className="gap-y-md">
              {state.initialQuestionsWithAnswers.map((iq) => {
                return (
                  <YStack className="gap-y-xs" key={iq.questionId}>
                    <Text type="h6">{getIQTitle(iq)}</Text>
                    {match(iq)
                      .with({ type: 'multi_choice' }, (iq: InitialQuestionWithAnswerV2 & { type: 'multi_choice' }) => {
                        return iq.answer.map((a) => (
                          <Text color="secondary" key={a.id}>
                            - {a.value}
                          </Text>
                        ));
                      })
                      .with({ type: 'text' }, (iq: InitialQuestionWithAnswerV2 & { type: 'text' }) => (
                        <Text color="secondary">{iq.answer || 'Not answered'}</Text>
                      ))
                      .with({ type: 'secure_note' }, (iq) => (
                        <Text color="secondary">{iq.secureNoteId ? 'Provided ✅' : 'Skipped ❌'}</Text>
                      ))
                      .otherwise(() => null)}
                  </YStack>
                );
              })}
            </YStack>
          </SectionCard>
        </YStack>
      </YStack>
      <YStack className="lg:col-span-2 md:col-span-2">
        <SectionCard title="Task starts">
          <YStack className="gap-y-md">
            <YStack>
              <Text color="secondary" type="p-sm">
                {taskStartText}
              </Text>
            </YStack>
            <Divider />
            <YStack>
              <YStack flexShrink={1} pl="sm">
                {taskComplexityText ? <Text type="h4">{taskComplexityText}</Text> : null}
                {isShortOnCredits && showCredits ? (
                  <Text type="p-sm">
                    {pricingTerminology.cost_label}: {taskCreditCost} {pricingTerminology.unit_term_plural} • Balance:{' '}
                    {balance}
                  </Text>
                ) : (
                  <Text type="p-sm">{creditText.remaining}</Text>
                )}
              </YStack>
            </YStack>
            <CheckoutButton />
            <BuyCreditsModal
              closeModal={() => {
                refetchCredits();
                setBuyCreditsModalOpen(false);
              }}
              isOpen={isBuyCreditsModalOpen}
              minimumCredits={isShortOnCredits ? taskCreditCost - balance : undefined}
            />
            <PaywallModal
              isFrictionlessUser={isFrictionlessSignupUser(client)}
              isOpen={isPaywallModalOpen}
              minimumCredits={taskCreditCost || undefined}
              onClose={hidePaywallModal}
              onSuccess={onPaywallSuccess}
            />
          </YStack>
        </SectionCard>
      </YStack>
    </YStack>
  );
};
